import { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Text,
  VisuallyHidden,
} from "@telegram-apps/telegram-ui";
import { useI18n } from "../../utils/i18n/I18nProvider";
import {
  useGameStore,
  selectEnergy,
  selectEnergyData,
} from "../../store/gameStore";
import ApiService from "../../handlers/apiService";
import { BoostTypeEnum, UserBoosts } from "../../handlers/types";
import { openInvoice } from "@telegram-apps/sdk";
import "./EnergyPopup.css";
import { DialogDescription } from "@radix-ui/react-dialog";
import { DialogTitle } from "@radix-ui/react-dialog";
import StageService from "../../services/StageService";

interface EnergyPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Energy popup component that displays when user runs out of energy
 */
export function EnergyPopup({ isOpen, onClose }: EnergyPopupProps) {
  const { t } = useI18n();
  const energy = useGameStore(selectEnergy);
  const energyData = useGameStore(selectEnergyData);
  const refreshEnergyFromApi = useGameStore.getState().refreshEnergyFromApi;
  const increaseEnergy = useGameStore.getState().increaseEnergy;
  const refillEnergyFull = useGameStore.getState().refillEnergyFull;
  const [isLoading, setIsLoading] = useState(false);
  const [userBoosts, setUserBoosts] = useState<UserBoosts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nextEnergyTime, setNextEnergyTime] = useState(
    energyData?.nextRestore || 0
  );

  // Get the API client
  const api = ApiService.getInstance();

  // Fetch available boosts when the popup opens
  useEffect(() => {
    if (isOpen) {
      fetchUserBoosts();
    }
  }, [isOpen]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Update timer every second
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      const timeLeft = energyData?.nextRestore || 0;
      setNextEnergyTime(timeLeft);

      // If time is up, refresh energy data
      if (timeLeft <= 0) {
        refreshEnergyFromApi().then(() => {
          // Close the popup if user has energy now
          if (energy > 0) {
            onClose();
          }
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, onClose, energyData, energy, refreshEnergyFromApi]);

  const fetchUserBoosts = async () => {
    try {
      setIsLoading(true);
      const boosts = await api.api.boostsList();
      setUserBoosts(boosts);
    } catch (error) {
      console.error("Failed to fetch user boosts:", error);
      setError(t("common.error_fetching_data"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefillOne = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Find energy_one boost from array
      const energyOneBoost = userBoosts?.boosts?.find(
        (boost) => boost.type === BoostTypeEnum.EnergyOne
      );

      // Check if user has this boost
      if (energyOneBoost && energyOneBoost.count > 0) {
        // Use the existing boost
        const result = await api.api.boostsUseCreate({
          boostType: BoostTypeEnum.EnergyOne,
          runId: StageService.getInstance().getRunId(),
        });
        if (result.success) {
          // Update energy synchronously
          increaseEnergy(1);
          onClose();
        } else {
          setError(t("energy.use_failed"));
        }
      } else {
        // Try to purchase a boost
        const { invoiceUrl } = await api.api.boostsBuyCreate({
          boostType: BoostTypeEnum.EnergyOne,
        });

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");

          if (status === "paid") {
            // Wait for 1 second before using the boost
            await new Promise((resolve) => setTimeout(resolve, 1000));

            await api.api.boostsUseCreate({
              boostType: BoostTypeEnum.EnergyOne,
              runId: StageService.getInstance().getRunId(),
            });

            // Update energy synchronously and refresh boosts
            increaseEnergy(1);
            await fetchUserBoosts();
            onClose();
          } else if (status === "failed") {
            setError(t("energy.payment_failed"));
          }
          // For cancelled payments, just keep the popup open
        } else {
          setError(t("energy.invoice_not_available"));
        }
      }
    } catch (error) {
      console.error("Failed to refill energy:", error);
      setError(t("energy.refill_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefillFull = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Find energy_full boost from array
      const energyFullBoost = userBoosts?.boosts?.find(
        (boost) => boost.type === BoostTypeEnum.EnergyFull
      );

      // Check if user has this boost
      if (energyFullBoost && energyFullBoost.count > 0) {
        // Use the existing boost
        const result = await api.api.boostsUseCreate({
          boostType: BoostTypeEnum.EnergyFull,
          runId: StageService.getInstance().getRunId(),
        });
        if (result.success) {
          // Update energy synchronously
          refillEnergyFull();
          onClose();
        } else {
          setError(t("energy.use_failed"));
        }
      } else {
        // Try to purchase a boost
        const { invoiceUrl } = await api.api.boostsBuyCreate({
          boostType: BoostTypeEnum.EnergyFull,
        });

        if (invoiceUrl && openInvoice.isAvailable()) {
          const status = await openInvoice(invoiceUrl, "url");

          if (status === "paid") {
            // Wait for 1 second before using the boost
            await new Promise((resolve) => setTimeout(resolve, 1000));

            await api.api.boostsUseCreate({
              boostType: BoostTypeEnum.EnergyFull,
              runId: StageService.getInstance().getRunId(),
            });

            // Update energy synchronously and refresh boosts
            refillEnergyFull();
            await fetchUserBoosts();
            onClose();
          } else if (status === "failed") {
            setError(t("energy.payment_failed"));
          }
          // For cancelled payments, just keep the popup open
        } else {
          setError(t("energy.invoice_not_available"));
        }
      }
    } catch (error) {
      console.error("Failed to refill energy:", error);
      setError(t("energy.refill_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Determine button text based on whether user has boosts
  const energyOneBoost = userBoosts?.boosts?.find(
    (boost) => boost.type === BoostTypeEnum.EnergyOne
  );
  const energyFullBoost = userBoosts?.boosts?.find(
    (boost) => boost.type === BoostTypeEnum.EnergyFull
  );

  const hasEnergyOneBoost = energyOneBoost && energyOneBoost.count > 0;
  const hasEnergyFullBoost = energyFullBoost && energyFullBoost.count > 0;

  const energyOneButtonText = hasEnergyOneBoost
    ? t("energy.use_energy_one_boost", {
        count: energyOneBoost?.count,
      })
    : t("energy.buy_energy_one_boost");

  const energyFullButtonText = hasEnergyFullBoost
    ? t("energy.use_energy_full_boost", {
        count: energyFullBoost?.count,
      })
    : t("energy.buy_energy_full_boost");

  // Get generalized time message
  const getTimeMessage = (seconds: number): string => {
    if (seconds <= 0) return t("energy.next_energy_soon");

    // Less than 15 minutes
    if (seconds < 15 * 60) return t("energy.next_energy_few_minutes");

    // More than 1 hour
    if (seconds > 60 * 60) return t("energy.next_energy_more_than_hour");

    // Between 15 minutes and 1 hour
    return t("energy.next_energy_soon_hour");
  };

  return (
    <Modal
      className="energy-popup-modal"
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      aria-labelledby="energy-popup-title"
    >
      <VisuallyHidden>
        <DialogDescription>{t("energy.refill_explanation")}</DialogDescription>
        <DialogTitle>{t("common.no_energy")}</DialogTitle>
      </VisuallyHidden>

      <div
        className="energy-popup-wrapper"
        role="dialog"
        aria-modal="true"
        aria-labelledby="energy-popup-title"
      >
        {/* Energy icon */}
        <div className="energy-popup-icon" />

        {/* Title */}
        <Text id="energy-popup-title" className="energy-popup-title">
          {t("common.no_energy")}
        </Text>
        {/* Explanation text */}
        <Text className="energy-popup-description">
          {t("energy.refill_explanation")}
        </Text>
        {/* Time until next energy recharge */}
        <Text className="energy-popup-timer">
          {getTimeMessage(nextEnergyTime)}
        </Text>
        {/* Refill 1 button */}
        <Button
          onClick={handleRefillOne}
          size="l"
          stretched
          className="energy-popup-button"
          disabled={isLoading}
        >
          <span className="energy-popup-button-text">
            {energyOneButtonText}
          </span>
          <div className="energy-popup-star-icon" />
        </Button>
        {/* Refill Full button */}
        <Button
          onClick={handleRefillFull}
          size="l"
          stretched
          className="energy-popup-button"
          disabled={isLoading}
        >
          <span className="energy-popup-button-text">
            {energyFullButtonText}
          </span>
          <div className="energy-popup-star-icon" />
        </Button>
      </div>
    </Modal>
  );
}

/**
 * Format seconds into a human-readable time string (MM:SS)
 */
function formatTime(seconds: number): string {
  if (!seconds) return "00:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
}
